import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import router, { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import { getFantasticFansPaginated } from '@services/youtube-platform.service';
import CrawlStage from '@components/Presentation/main/PresentationBody/InteractiveInteractions/CrawlStage';
import useFonts from '@lib/hooks/useFonts';
import useInteractionSound from '@components/Presentation/hooks/useInteractionSound';

const SHOW_TOP_MAP = { 3: 3, 5: 5, 10: 10, all: Infinity };

const Credits = forwardRef(({ streamId, currentRosFeature, setShowNotification, isMuted }, ref) => {
  const [colors, setColors] = useState(currentRosFeature?.color || {});
  const joinType = useRouter().query?.joinType;

  const { getFontStyle } = useFonts();
  const fontFamily = getFontStyle(currentRosFeature?.setting?.FontStyle || 'Sans-Serif').fontFamily;
  const headingFontFamily = getFontStyle(currentRosFeature?.setting?.HeadingFont || 'Sans-Serif').fontFamily;

  const setting = currentRosFeature?.setting || {};
  const showTopN = SHOW_TOP_MAP[setting.creditsShowTop || 'all'] ?? Infinity;
  const speed = Number(setting.creditsSpeed) || 60;
  const fontSizeScale = (Number(setting.creditsFontSize) || 1) * 2;
  const episodeLabel = setting.creditsEpisodeLabel ?? 'Thank You';
  const topLabel = setting.creditsTopLabel ?? 'Top Commenters';
  const closingLine = setting.creditsClosingLine ?? 'See you next time!';

  const isScoreEnabled = setting.isCreditsSoundEnabled !== false;

  const { playSound, sound } = useInteractionSound({
    featureSettings: {
      ...currentRosFeature,
      type: 'credits',
      setting: { ...setting, isCreditsSoundEnabled: isScoreEnabled },
    },
    soundKey: 'creditsSoundOptions',
    isEnabledKey: 'isCreditsSoundEnabled',
    isMuted,
    options: {
      volume: 0.4,
      playbackRate: 1,
      loop: true,
    },
  });

  const hasStartedScoreRef = useRef(false);
  useEffect(() => {
    if (!sound || hasStartedScoreRef.current || !isScoreEnabled) return;
    hasStartedScoreRef.current = true;
    playSound();
  }, [sound, playSound, isScoreEnabled]);

  useEffect(() => {
    if (!sound) return undefined;
    return () => {
      sound.stop();
      sound.unload?.();
    };
  }, [sound]);

  const fansData = useQuery({
    queryKey: ['fans', streamId, currentRosFeature?.id, setting.creditsShowTop],
    queryFn: () => getFantasticFansPaginated(streamId, { filter: setting.creditsShowTop }),
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (currentRosFeature?.color) setColors(currentRosFeature.color);
  }, [currentRosFeature?.color]);

  const allFans = (fansData?.data?.status && fansData?.data?.entity?.rows) || [];
  const topFans = showTopN === Infinity ? allFans : allFans.slice(0, showTopN);
  const restFans = showTopN === Infinity ? [] : allFans.slice(showTopN);

  const stopCurrentFeature = () => {
    router.push({ pathname: `/streams/${streamId}/ros`, query: joinType && { joinType } });
  };
  useImperativeHandle(ref, () => ({ stopCurrentFeature }));

  return (
    <div className="absolute inset-0">
      <div className="w-full h-full">
        <CrawlStage
          title={currentRosFeature?.title || ''}
          episodeLabel={episodeLabel}
          topLabel={topLabel}
          topFans={topFans}
          restFans={restFans}
          showCount={setting.creditsShowCount === true}
          closingLine={closingLine}
          speed={speed}
          textColor={colors.creditsTextColor || '#FFFFFF'}
          headingColor={colors.creditsHeadingColor || '#FFFFFF'}
          fontFamily={fontFamily}
          headingFontFamily={headingFontFamily}
          fontSizeScale={fontSizeScale}
        />
      </div>
    </div>
  );
});

Credits.displayName = 'Credits';

export default Credits;
